(() => {
  const GIFT_VARIANT_ID = 46527491997846;
  const GIFT_PRODUCT_HANDLE = "lgnd-jet-tag-1";
  const GIFT_PRODUCT_TITLE = "LGND Jet Tag";
  const THRESHOLD_CENTS = 5000;
  const GIFT_PROPERTY = "_free_gift";

  let syncing = false;
  let syncTimer;

  const originalFetch = window.fetch.bind(window);

  /*
   * Retrieve the current Shopify cart.
   */
  async function getCart() {
    const response = await originalFetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        method: "GET",
        headers: {
          Accept: "application/json"
        }
      }
    );

    if (!response.ok) {
      throw new Error("Unable to retrieve the cart.");
    }

    return response.json();
  }

  /*
   * Identify a JetTag added by this automation.
   */
  function isAutomaticGift(item) {
    return (
      Number(item.variant_id) === GIFT_VARIANT_ID &&
      item.properties &&
      String(item.properties[GIFT_PROPERTY]) === "true"
    );
  }

  /*
   * Calculate the qualifying merchandise subtotal.
   * JetTags do not count toward the $50 requirement.
   */
  function getQualifyingSubtotal(cart) {
    return cart.items.reduce((subtotal, item) => {
      if (Number(item.variant_id) === GIFT_VARIANT_ID) {
        return subtotal;
      }

      return subtotal + Number(item.final_line_price || 0);
    }, 0);
  }

  /*
   * Add one JetTag with a hidden free-gift property.
   */
  async function addGift() {
    const response = await originalFetch(
      `${window.Shopify.routes.root}cart/add.js`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          items: [
            {
              id: GIFT_VARIANT_ID,
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: "true"
              }
            }
          ]
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        "The free JetTag could not be added. It may be sold out."
      );
    }
  }

  /*
   * Remove all automatically added JetTags.
   * This is used only when the cart falls below $50.
   */
  async function removeGiftLines(giftItems) {
    const updates = {};

    giftItems.forEach((item) => {
      updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return;

    const response = await originalFetch(
      `${window.Shopify.routes.root}cart/update.js`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          updates
        })
      }
    );

    if (!response.ok) {
      throw new Error("Unable to remove the free JetTag.");
    }
  }

  /*
   * Tell Prestige that the cart changed.
   */
  function refreshCartDisplay() {
    document.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          source: "free-gift"
        }
      })
    );

    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {
          source: "free-gift"
        }
      })
    );

    /*
     * Reliably refresh the full cart page after a gift change.
     */
    if (
      window.location.pathname.replace(/\/$/, "") ===
      "/cart"
    ) {
      window.location.reload();
    }
  }

  /*
   * Add or remove the gift based on cart value.
   *
   * This intentionally does not force a quantity of two
   * back to one. That behavior can conflict with Prestige's
   * optimistic cart updates and create an infinite loop.
   */
  async function syncFreeGift() {
    if (syncing) return;

    syncing = true;
    let cartChanged = false;

    try {
      const cart = await getCart();

      const giftItems = cart.items.filter(
        isAutomaticGift
      );

      const qualifyingSubtotal =
        getQualifyingSubtotal(cart);

      const customerQualifies =
        qualifyingSubtotal >= THRESHOLD_CENTS;

      if (
        customerQualifies &&
        giftItems.length === 0
      ) {
        await addGift();
        cartChanged = true;
      } else if (
        !customerQualifies &&
        giftItems.length > 0
      ) {
        await removeGiftLines(giftItems);
        cartChanged = true;
      }

      if (cartChanged) {
        refreshCartDisplay();
      }
    } catch (error) {
      console.warn(
        "[Free JetTag]",
        error.message
      );
    } finally {
      syncing = false;
    }
  }

  function scheduleGiftSync() {
    window.clearTimeout(syncTimer);

    syncTimer = window.setTimeout(() => {
      syncFreeGift();
    }, 250);
  }

  /*
   * Detect Shopify AJAX cart changes.
   */
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    const request =
      typeof args[0] === "string"
        ? args[0]
        : args[0] && args[0].url
          ? args[0].url
          : "";

    const isCartMutation =
      /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(
        request
      );

    if (
      !syncing &&
      isCartMutation &&
      response.ok
    ) {
      scheduleGiftSync();
    }

    return response;
  };

  /*
   * Normalize visible text before matching it.
   */
  function normalizeText(element) {
    return (element.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  /*
   * Find cart drawer and full cart-page containers.
   */
  function getCartRoots() {
    return document.querySelectorAll(
      [
        "cart-drawer",
        "#cart-drawer",
        ".cart-drawer",
        "[id*='CartDrawer']",
        "form[action='/cart']",
        "form[action$='/cart']"
      ].join(",")
    );
  }

  /*
   * Find all rendered JetTag rows.
   */
  function findGiftRows() {
    const rows = new Set();

    const giftMarkers = document.querySelectorAll(
      [
        `a[href*="/products/${GIFT_PRODUCT_HANDLE}"]`,
        `a[href*="variant=${GIFT_VARIANT_ID}"]`,
        `[data-variant-id="${GIFT_VARIANT_ID}"]`,
        `[data-product-variant-id="${GIFT_VARIANT_ID}"]`
      ].join(",")
    );

    giftMarkers.forEach((giftMarker) => {
      const giftRow = giftMarker.closest(
        [
          "line-item",
          ".line-item",
          ".cart-item",
          "[data-line-item]",
          "[data-cart-item]",
          "tr"
        ].join(",")
      );

      if (giftRow) {
        rows.add(giftRow);
      }
    });

    /*
     * Fall back to the exact product title.
     */
    getCartRoots().forEach((cartRoot) => {
      cartRoot
        .querySelectorAll("a, h2, h3, h4, p, span")
        .forEach((titleElement) => {
          const title = normalizeText(titleElement);

          if (
            title.toLowerCase() !==
            GIFT_PRODUCT_TITLE.toLowerCase()
          ) {
            return;
          }

          const giftRow = titleElement.closest(
            [
              "line-item",
              ".line-item",
              ".cart-item",
              "[data-line-item]",
              "[data-cart-item]",
              "tr"
            ].join(",")
          );

          if (giftRow) {
            rows.add(giftRow);
          }
        });
    });

    return Array.from(rows);
  }

  /*
   * Check whether an element is inside a JetTag row.
   */
  function getContainingGiftRow(element) {
    return findGiftRows().find((giftRow) => {
      return (
        giftRow === element ||
        giftRow.contains(element)
      );
    });
  }

  /*
   * Keep the JetTag first in the cart drawer.
   */
  function moveGiftToTop() {
    const giftRows = findGiftRows();

    giftRows.forEach((giftRow) => {
      const productList = giftRow.parentElement;

      if (!productList) return;

      const firstProductRow = Array.from(
        productList.children
      ).find((child) => {
        return (
          child.matches(
            [
              "line-item",
              ".line-item",
              ".cart-item",
              "[data-line-item]",
              "[data-cart-item]",
              "tr"
            ].join(",")
          ) ||
          /\bremove\b/i.test(normalizeText(child))
        );
      });

      if (
        firstProductRow &&
        firstProductRow !== giftRow
      ) {
        productList.insertBefore(
          giftRow,
          firstProductRow
        );
      }
    });
  }

  /*
   * Hide and disable the JetTag quantity selector.
   */
  function lockGiftQuantityControls() {
    const giftRows = findGiftRows();

    const quantityWrapperSelector = [
      "quantity-selector",
      "quantity-input",
      ".quantity-selector",
      ".quantity-input",
      "[data-quantity-selector]",
      "[class*='quantity-selector']",
      "[class*='quantity-input']"
    ].join(",");

    const quantityControlSelector = [
      quantityWrapperSelector,
      "button[name='plus']",
      "button[name='minus']",
      "input[name='quantity']",
      "input[name='updates[]']",
      "[data-quantity-plus]",
      "[data-quantity-minus]"
    ].join(",");

    giftRows.forEach((giftRow) => {
      giftRow
        .querySelectorAll(quantityControlSelector)
        .forEach((control) => {
          if (
            control.matches(
              "input[name='quantity'], input[name='updates[]']"
            )
          ) {
            control.value = "1";
            control.disabled = true;
            control.setAttribute("aria-disabled", "true");
          }

          const quantityWrapper =
            control.closest(
              quantityWrapperSelector
            ) || control;

          quantityWrapper.style.setProperty(
            "display",
            "none",
            "important"
          );
        });
    });
  }

  /*
   * Block gift quantity clicks before Prestige receives them.
   */
  function blockGiftQuantityClick(event) {
    const quantityControl = event.target.closest(
      [
        "quantity-selector button",
        "quantity-input button",
        ".quantity-selector button",
        ".quantity-input button",
        "[data-quantity-selector] button",
        "[class*='quantity-selector'] button",
        "[class*='quantity-input'] button",
        "button[name='plus']",
        "button[name='minus']",
        "[data-quantity-plus]",
        "[data-quantity-minus]"
      ].join(",")
    );

    if (!quantityControl) return;

    const giftRow =
      getContainingGiftRow(quantityControl);

    if (!giftRow) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  /*
   * Block keyboard, input, and change events on the
   * JetTag quantity field.
   */
  function blockGiftQuantityChange(event) {
    const quantityInput = event.target.closest(
      [
        "input[name='quantity']",
        "input[name='updates[]']",
        "quantity-input input",
        "quantity-selector input",
        ".quantity-input input",
        ".quantity-selector input"
      ].join(",")
    );

    if (!quantityInput) return;

    const giftRow =
      getContainingGiftRow(quantityInput);

    if (!giftRow) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    quantityInput.value = "1";
  }

  /*
   * Hide Shopify's original Free Gift discount labels.
   * The actual automatic discount remains active.
   */
  function hideOriginalDiscountLabels() {
    const freeGiftPattern =
      /^free gift(?:\s*\([^)]*\))?$/i;

    getCartRoots().forEach((cartRoot) => {
      cartRoot
        .querySelectorAll("li, span, p, div")
        .forEach((element) => {
          /*
           * Preserve our custom green FREE GIFT badge.
           */
          if (
            element.matches(
              "[data-free-gift-badge]"
            ) ||
            element.closest(
              "[data-free-gift-badge]"
            )
          ) {
            return;
          }

          const text = normalizeText(element);

          if (!freeGiftPattern.test(text)) {
            return;
          }

          const childContainsSameLabel =
            Array.from(element.children).some(
              (child) => {
                return freeGiftPattern.test(
                  normalizeText(child)
                );
              }
            );

          if (childContainsSameLabel) {
            return;
          }

          const labelContainer =
            element.closest(
              [
                "[class*='discount']",
                "[class*='badge']",
                "[class*='pill']",
                "li"
              ].join(",")
            ) || element;

          labelContainer.style.setProperty(
            "display",
            "none",
            "important"
          );
        });
    });
  }

  /*
   * Style the green FREE GIFT badge.
   */
  function styleFreeGiftBadge(badge) {
    badge.setAttribute(
      "data-free-gift-badge",
      "true"
    );

    badge.removeAttribute("hidden");

    badge.style.setProperty(
      "display",
      "inline-flex",
      "important"
    );

    badge.style.setProperty(
      "align-items",
      "center",
      "important"
    );

    badge.style.setProperty(
      "justify-content",
      "center",
      "important"
    );

    badge.style.setProperty(
      "width",
      "auto",
      "important"
    );

    badge.style.setProperty(
      "min-width",
      "auto",
      "important"
    );

    badge.style.setProperty(
      "height",
      "auto",
      "important"
    );

    badge.style.setProperty(
      "margin",
      "0 0 0 8px",
      "important"
    );

    badge.style.setProperty(
      "padding",
      "5px 8px",
      "important"
    );

    badge.style.setProperty(
      "border",
      "0",
      "important"
    );

    badge.style.setProperty(
      "border-radius",
      "4px",
      "important"
    );

    badge.style.setProperty(
      "background",
      "#16A34A",
      "important"
    );

    badge.style.setProperty(
      "color",
      "#FFFFFF",
      "important"
    );

    badge.style.setProperty(
      "font-family",
      "inherit",
      "important"
    );

    badge.style.setProperty(
      "font-size",
      "11px",
      "important"
    );

    badge.style.setProperty(
      "font-style",
      "normal",
      "important"
    );

    badge.style.setProperty(
      "font-weight",
      "800",
      "important"
    );

    badge.style.setProperty(
      "line-height",
      "1",
      "important"
    );

    badge.style.setProperty(
      "letter-spacing",
      "0.03em",
      "important"
    );

    badge.style.setProperty(
      "text-transform",
      "uppercase",
      "important"
    );

    badge.style.setProperty(
      "text-decoration",
      "none",
      "important"
    );

    badge.style.setProperty(
      "white-space",
      "nowrap",
      "important"
    );

    badge.style.setProperty(
      "opacity",
      "1",
      "important"
    );

    badge.style.setProperty(
      "visibility",
      "visible",
      "important"
    );
  }

  /*
   * Replace the JetTag entries and multiplier component
   * with one green FREE GIFT badge.
   *
   * $0.00 and the struck-through $15.00 remain visible.
   */
  function updateJetTagBadges() {
    const giftRows = findGiftRows();

    giftRows.forEach((giftRow) => {
      const entriesBadges =
        giftRow.querySelectorAll(
          ".cart__entries-badge, .entries-badge"
        );

      entriesBadges.forEach(
        (entriesBadge, index) => {
          /*
           * Hide duplicate responsive giveaway badges.
           */
          if (index > 0) {
            entriesBadge.style.setProperty(
              "display",
              "none",
              "important"
            );

            return;
          }

          if (
            entriesBadge.getAttribute(
              "data-free-gift-badge"
            ) !== "true" ||
            normalizeText(entriesBadge) !==
              "FREE GIFT"
          ) {
            entriesBadge.replaceChildren(
              document.createTextNode(
                "FREE GIFT"
              )
            );
          }

          styleFreeGiftBadge(entriesBadge);
        });
    });
  }

  /*
   * Apply all JetTag presentation and interaction rules.
   */
  function cleanCartPresentation() {
    moveGiftToTop();
    lockGiftQuantityControls();
    hideOriginalDiscountLabels();
    updateJetTagBadges();
  }

  function initializeFreeGift() {
    /*
     * Capture quantity interactions before Prestige handles them.
     */
    document.addEventListener(
      "click",
      blockGiftQuantityClick,
      true
    );

    document.addEventListener(
      "input",
      blockGiftQuantityChange,
      true
    );

    document.addEventListener(
      "change",
      blockGiftQuantityChange,
      true
    );

    syncFreeGift();
    cleanCartPresentation();

    /*
     * Prestige dynamically rebuilds the cart drawer.
     */
    const observer =
      new MutationObserver(() => {
        cleanCartPresentation();
      });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initializeFreeGift
    );
  } else {
    initializeFreeGift();
  }
})();
